import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useRole } from "../../components/AppShell";
import { Empty } from "../../components/ui";
import { api } from "../../lib/api";

interface ActivityRow {
  id: string;
  actorId: string;
  actorName: string;
  module: string;
  action: string;
  targetId: string | null;
  label: string | null;
  detail: Record<string, unknown> | null;
  at: string;
}

const MODULES: Record<string, string> = {
  contacts: "Contactos",
  conversations: "Conversaciones",
  cases: "Postventa",
  quotes: "Cotizaciones",
  catalog: "Catálogo",
  knowledge: "Conocimiento",
  lumi: "Lumi",
  team: "Equipo",
  whatsapp: "WhatsApp",
  company: "Empresa",
  support: "Soporte de Orbis",
};

/** What each change was, as a sentence that the name completes. */
const ACTION: Record<string, string> = {
  "contact.created": "creó el contacto",
  "contact.updated": "editó el contacto",
  "contact.imported": "importó contactos",
  "organization.created": "creó la organización",
  "organization.updated": "editó la organización",
  "conversation.assigned": "asignó la conversación con",
  "conversation.status": "cambió el estado de la conversación con",
  "conversation.template": "envió una plantilla a",
  "case.created": "abrió el caso",
  "case.updated": "editó el caso",
  "case.note": "agregó una nota al caso",
  "case.deleted": "eliminó un caso",
  "case.statuses": "cambió los estados de postventa",
  "part_request.created": "pidió el repuesto",
  "part_request.updated": "editó el pedido de repuesto",
  "part_request.note": "agregó una nota al pedido de repuesto",
  "part_request.deleted": "eliminó un pedido de repuesto",
  "part_request.statuses": "cambió los estados de los repuestos",
  "quote.created": "creó la cotización",
  "quote.updated": "editó la cotización",
  "quote.sent": "envió la cotización",
  "quote.deleted": "eliminó una cotización",
  "quote.settings": "cambió los ajustes de cotizaciones",
  "quote.letterhead": "subió el membrete de cotizaciones",
  "quote.letterhead_removed": "quitó el membrete de cotizaciones",
  "product.created": "agregó al catálogo",
  "product.updated": "editó en el catálogo",
  "product.deleted": "eliminó un producto del catálogo",
  "product.image": "subió una foto de",
  "product.imported": "importó productos al catálogo",
  "sales_point.created": "agregó el punto de venta",
  "sales_point.updated": "editó el punto de venta",
  "sales_point.deleted": "eliminó un punto de venta",
  "sales_point.imported": "importó puntos de venta",
  "knowledge.created": "agregó al conocimiento",
  "knowledge.updated": "editó en el conocimiento",
  "knowledge.deleted": "eliminó un documento del conocimiento",
  "knowledge.file": "subió el archivo de",
  "knowledge.file_removed": "quitó el archivo de un documento",
  "lumi.switched": "cambió si Lumi atiende",
  "lumi.config": "cambió la configuración de Lumi",
  "lumi.setup_done": "terminó la configuración guiada de Lumi",
  "team.visibility": "cambió qué ve cada asesor",
  "team.areas": "cambió las áreas de atención",
  "team.invited": "invitó a",
  "team.invitation_cancelled": "canceló una invitación",
  "team.role": "cambió el rol de",
  "team.removed": "quitó a alguien del equipo",
  "team.two_factor_reset": "quitó el código de verificación de",
  "quick_reply.created": "creó la respuesta rápida",
  "quick_reply.updated": "editó la respuesta rápida",
  "quick_reply.deleted": "eliminó una respuesta rápida",
  "template.updated": "editó la plantilla",
  "template.synced": "actualizó las plantillas desde Meta",
  "company.updated": "cambió los datos de la empresa",
  "support.granted": "dio acceso al soporte de Orbis",
  "support.revoked": "quitó el acceso al soporte de Orbis",
  "support.viewed": "miró como soporte",
};

/** Field names as the panel calls them, for "cambió: nombre, correo". */
const FIELD: Record<string, string> = {
  name: "nombre",
  email: "correo",
  type: "tipo",
  tags: "etiquetas",
  province: "provincia",
  notes: "notas",
  organizationId: "organización",
  title: "título",
  description: "descripción",
  statusId: "estado",
  assigneeId: "responsable",
  model: "modelo",
  serial: "serie",
  price: "precio",
  category: "categoría",
  specs: "especificaciones",
  status: "estado",
  body: "texto",
  shortcut: "atajo",
  role: "rol",
};

const SUPPORT_WHAT: Record<string, string> = {
  conversations: "la lista de conversaciones",
  conversation: "una conversación",
  contacts: "la lista de contactos",
  contact: "un contacto",
  cases: "la lista de casos",
  case: "un caso",
};
const STATUS: Record<string, string> = { bot: "con Lumi", human: "con un asesor", closed: "cerrada" };
const ROLE: Record<string, string> = { owner: "propietario", admin: "administrador", agent: "asesor" };

function sentence(r: ActivityRow) {
  const d = r.detail ?? {};
  const parts = [ACTION[r.action] ?? r.action];
  if (r.action === "support.viewed" && typeof d.what === "string") parts.push(SUPPORT_WHAT[d.what] ?? d.what);
  else if (r.label) parts.push(r.label);
  else if (r.module === "conversations") parts.push("un cliente");
  const extra: string[] = [];
  if ("to" in d) extra.push(d.to ? `a ${d.to}` : "sin responsable");
  if (typeof d.status === "string") extra.push(`ahora ${STATUS[d.status] ?? d.status}`);
  if (typeof d.role === "string") extra.push(`ahora ${ROLE[d.role] ?? d.role}`);
  if (typeof d.enabled === "boolean") extra.push(d.enabled ? "encendida" : "apagada");
  if (typeof d.hours === "number") extra.push(d.hours >= 48 ? `por ${d.hours / 24} días` : `por ${d.hours} horas`);
  if (typeof d.created === "number") extra.push(`${d.created} nuevos`);
  if (typeof d.updated === "number") extra.push(`${d.updated} actualizados`);
  if (Array.isArray(d.fields) && d.fields.length && !["support.granted"].includes(r.action))
    extra.push(`cambió: ${d.fields.map((f) => FIELD[String(f)] ?? String(f)).join(", ")}`);
  return `${parts.join(" ")}${extra.length ? ` (${extra.join("; ")})` : ""}`;
}

const day = new Intl.DateTimeFormat("es-CR", {
  weekday: "long",
  day: "numeric",
  month: "long",
  timeZone: "America/Costa_Rica",
});
const hour = new Intl.DateTimeFormat("es-CR", { hour: "numeric", minute: "2-digit", timeZone: "America/Costa_Rica" });

/** Auditoría: its own section, for owners and admins (the API refuses everyone else). */
export function AuditPage() {
  const role = useRole();
  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Auditoría</h1>
          <p className="muted">Quién cambió qué y cuándo en la empresa.</p>
        </div>
      </div>
      {role === "owner" || role === "admin" ? <AuditLog /> : <Empty title="Solo para propietarios y administradores" />}
    </div>
  );
}

/**
 * The company's activity log (f6-14): who changed what and when. New changes arrive live.
 */
function AuditLog() {
  const [filter, setFilter] = useState({ actor: "", module: "", from: "", to: "" });
  const params = new URLSearchParams();
  if (filter.actor) params.set("actor", filter.actor);
  if (filter.module) params.set("module", filter.module);
  // Costa Rica days, whole: from its midnight to the end of the last day.
  if (filter.from) params.set("from", `${filter.from}T00:00:00-06:00`);
  if (filter.to) params.set("to", `${filter.to}T23:59:59.999-06:00`);
  const actors = useQuery({
    queryKey: ["activity", "actors"],
    queryFn: () => api.get<{ items: { actorId: string; actorName: string }[] }>("/activity/actors"),
  });
  const log = useInfiniteQuery({
    queryKey: ["activity", "list", params.toString()],
    queryFn: ({ pageParam }) =>
      api.get<{ items: ActivityRow[]; next: number | null }>(
        `/activity?${params.toString()}${pageParam ? `&before=${pageParam}` : ""}`,
      ),
    initialPageParam: 0,
    getNextPageParam: (last) => last.next ?? undefined,
  });
  const rows = log.data?.pages.flatMap((p) => p.items) ?? [];
  const byDay: { day: string; rows: ActivityRow[] }[] = [];
  for (const r of rows) {
    // "sábado, 31 de octubre" → "Sábado, 31 de octubre".
    const text = day.format(new Date(r.at));
    const d = text.charAt(0).toUpperCase() + text.slice(1);
    const last = byDay.at(-1);
    if (last?.day === d) last.rows.push(r);
    else byDay.push({ day: d, rows: [r] });
  }

  return (
    <div className="card">
      <div className="card-body activity">
        <p className="muted">Muestra qué se cambió, no el contenido. Se guarda un año.</p>
        <div className="activity-filters">
          <label className="field">
            <span>Persona</span>
            <select
              className="select"
              value={filter.actor}
              onChange={(e) => setFilter({ ...filter, actor: e.target.value })}
            >
              <option value="">Todas</option>
              {(actors.data?.items ?? []).map((a) => (
                <option key={a.actorId} value={a.actorId}>
                  {a.actorId.startsWith("staff:") ? `${a.actorName} (soporte de Orbis)` : a.actorName}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Módulo</span>
            <select
              className="select"
              value={filter.module}
              onChange={(e) => setFilter({ ...filter, module: e.target.value })}
            >
              <option value="">Todos</option>
              {Object.entries(MODULES).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Desde</span>
            <input
              className="input"
              type="date"
              placeholder="Ej.: 2026-10-01"
              value={filter.from}
              onChange={(e) => setFilter({ ...filter, from: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Hasta</span>
            <input
              className="input"
              type="date"
              placeholder="Ej.: 2026-10-31"
              value={filter.to}
              onChange={(e) => setFilter({ ...filter, to: e.target.value })}
            />
          </label>
        </div>
        {log.isPending ? (
          <p className="muted">Cargando…</p>
        ) : !rows.length ? (
          <Empty title="Nada registrado">
            <p className="muted">Los cambios que hagan en Orbis van a aparecer aquí.</p>
          </Empty>
        ) : (
          <div className="activity-days">
            {byDay.map((g) => (
              <section key={g.day}>
                <h4>{g.day}</h4>
                <ul>
                  {g.rows.map((r) => (
                    <li key={r.id}>
                      <span className="activity-module">{MODULES[r.module] ?? r.module}</span>
                      <span>
                        <strong>
                          {r.actorId.startsWith("staff:") ? `${r.actorName} (soporte de Orbis)` : r.actorName}
                        </strong>{" "}
                        {sentence(r)}
                      </span>
                      <time className="faint" dateTime={r.at}>
                        {hour.format(new Date(r.at))}
                      </time>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
        {log.hasNextPage && (
          <div>
            <button
              type="button"
              className="btn btn-sm"
              disabled={log.isFetchingNextPage}
              onClick={() => log.fetchNextPage()}
            >
              {log.isFetchingNextPage ? "Cargando…" : "Ver más"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
